import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowRight, CheckCircle2, Users, Briefcase, Heart, TrendingUp, Award, Wrench, Shield, Calendar, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const partnerTranslations = {
  de: {
    heroTitle: 'Partner',
    heroTitle2: 'werden',
    heroDesc: 'Werden Sie Teil unseres wachsenden Netzwerks. Als Gravante Partner profitieren Sie von attraktiven Konditionen und einer starken Marke.',
    applyNow: 'Jetzt bewerben',
    benefits: 'Vorteile',
    partners: 'Partner',
    cities: 'Städte',
    growth: 'Wachstum',
    partnerBenefits: 'Vorteile als Gravante Partner',
    benefitsDesc: 'Was Sie als Partner bei uns erwartet',
    attractiveComm: 'Attraktive Provision',
    commDesc: 'Profitieren Sie von überdurchschnittlichen Vermittlungsprovisionen',
    premiumProducts: 'Premium Produkte',
    productsDesc: 'Arbeiten Sie mit den besten Marken am Markt',
    training: 'Schulungen',
    trainingDesc: 'Regelmäßige Weiterbildungen und Produktschulungen',
    marketing: 'Marketingunterstützung',
    marketingDesc: 'Professionelle Werbematerialien und Leads',
    techSupport: 'Technischer Support',
    supportDesc: '24/7 Support für Sie und Ihre Kunden',
    network: 'Starkes Netzwerk',
    networkDesc: 'Teil eines wachsenden Partnernetzwerks werden',
    applyForm: 'Bewerbungsformular',
    formDesc: 'Füllen Sie das Formular aus und wir melden uns bei Ihnen',
    company: 'Firma',
    contactPerson: 'Ansprechpartner',
    phone: 'Telefon',
    region: 'Region/Einzugsgebiet',
    experience: 'Erfahrung',
    message: 'Nachricht',
    submit: 'Bewerbung absenden',
    sending: 'Wird gesendet...',
    successTitle: 'Vielen Dank!',
    successDesc: 'Wir haben Ihre Bewerbung erhalten und melden uns in Kürze bei Ihnen.',
    // Company partner section
    companyTitle1: 'Unternehmens',
    companyTitle2: 'partner',
    companySubtitle: 'Kooperieren Sie mit uns und profitieren Sie von gegenseitigen Synergien',
    cGrowth: 'Wachstumschancen',
    cGrowthDesc: 'Profitieren Sie von unserem expandierenden Netzwerk und gemeinsamen Projekten',
    cNetwork: 'Starkes Netzwerk',
    cNetworkDesc: 'Zugang zu unserem etablierten Partner- und Kundennetzwerk',
    cQuality: 'Qualitätsstandards',
    cQualityDesc: 'Gemeinsame Qualitätssicherung und Best-Practice-Austausch',
    cProject: 'Projektkooperation',
    cProjectDesc: 'Gemeinsame Durchführung großer Projekte und Aufträge',
    partnerReqSent: 'Ihre Partneranfrage wurde erfolgreich gesendet.',
    contactSoon: 'Wir melden uns schnellstmöglich bei Ihnen.',
    newRequest: 'Neue Anfrage senden',
    partnerReqTitle: 'Partneranfrage stellen',
    companyPlaceholder: 'Ihre Firma',
    namePlaceholder: 'Ihr Name',
    email: 'E-Mail',
    companyEmailPlaceholder: 'firma@email.de',
    phonePlaceholder: '+49 123 456 789',
    industry: 'Branche',
    industryPlaceholder: 'z.B. Elektroinstallation, Sanitär, etc.',
    companyMsgPlaceholder: 'Beschreiben Sie Ihr Unternehmen und Ihre Kooperationsinteressen...',
    sendPartnerReq: 'Partneranfrage senden',
    errorTitle: 'Fehler',
    errorDesc: 'Bitte versuchen Sie es erneut.',
    // Employee section
    employeeTitle1: 'Mitarbeiter ',
    employeeTitle2: 'werden',
    employeeSubtitle: 'Starten Sie Ihre Karriere bei Gravante – einem der führenden Unternehmen im Bereich erneuerbare Energien',
    eFairPay: 'Faire Bezahlung',
    eFairPayDesc: 'Überdurchschnittliche Vergütung und pünktliche Auszahlung',
    eFlexHours: 'Flexible Arbeitszeiten',
    eFlexHoursDesc: 'Work-Life-Balance und planbare Arbeitszeiten',
    eFurtherEdu: 'Weiterbildung',
    eFurtherEduDesc: 'Regelmäßige Schulungen und Zertifizierungsmöglichkeiten',
    eSecureJob: 'Sicherer Arbeitsplatz',
    eSecureJobDesc: 'Langfristige Perspektiven in einem wachsenden Unternehmen',
    positions: ['Dachdecker (m/w/d)', 'Elektriker (m/w/d)', 'Heizungsbauer (m/w/d)', 'Solartechniker (m/w/d)', 'Helfer (m/w/d)', 'Projektleiter (m/w/d)'],
    openPositions: 'Offene Positionen',
    noteLabel: 'Hinweis:',
    noteDesc: 'Auch Quereinsteiger und Berufsanfänger sind herzlich willkommen!',
    applicationSent: 'Ihre Bewerbung wurde erfolgreich gesendet.',
    newApplication: 'Neue Bewerbung senden',
    nameLabel: 'Name',
    yourEmailPlaceholder: 'ihre@email.de',
    positionLabel: 'Position',
    positionPlaceholder: 'z.B. Dachdecker',
    workExp: 'Berufserfahrung (Jahre)',
    workExpPlaceholder: 'z.B. 5 Jahre',
    aboutYouPlaceholder: 'Erzählen Sie uns über sich...',
    submitApplication: 'Bewerbung absenden'
  },
  en: {
    heroTitle: 'Become a',
    heroTitle2: 'Partner',
    heroDesc: 'Become part of our growing network. As a Gravante partner, you benefit from attractive conditions and a strong brand.',
    applyNow: 'Apply Now',
    benefits: 'Benefits',
    partners: 'Partners',
    cities: 'Cities',
    growth: 'Growth',
    partnerBenefits: 'Benefits as a Gravante Partner',
    benefitsDesc: 'What you can expect as a partner with us',
    attractiveComm: 'Attractive Commission',
    commDesc: 'Benefit from above-average referral commissions',
    premiumProducts: 'Premium Products',
    productsDesc: 'Work with the best brands on the market',
    training: 'Training',
    trainingDesc: 'Regular further training and product training',
    marketing: 'Marketing Support',
    marketingDesc: 'Professional advertising materials and leads',
    techSupport: 'Technical Support',
    supportDesc: '24/7 support for you and your customers',
    network: 'Strong Network',
    networkDesc: 'Become part of a growing partner network',
    applyForm: 'Application Form',
    formDesc: 'Fill out the form and we will contact you',
    company: 'Company',
    contactPerson: 'Contact Person',
    phone: 'Phone',
    region: 'Region/Catchment Area',
    experience: 'Experience',
    message: 'Message',
    submit: 'Submit Application',
    sending: 'Sending...',
    successTitle: 'Thank You!',
    successDesc: 'We have received your application and will contact you shortly.',
    // Company partner section
    companyTitle1: 'Company',
    companyTitle2: ' Partners',
    companySubtitle: 'Cooperate with us and benefit from mutual synergies',
    cGrowth: 'Growth Opportunities',
    cGrowthDesc: 'Benefit from our expanding network and joint projects',
    cNetwork: 'Strong Network',
    cNetworkDesc: 'Access to our established partner and customer network',
    cQuality: 'Quality Standards',
    cQualityDesc: 'Joint quality assurance and best-practice exchange',
    cProject: 'Project Cooperation',
    cProjectDesc: 'Joint execution of large projects and orders',
    partnerReqSent: 'Your partner request has been sent successfully.',
    contactSoon: 'We will get back to you as soon as possible.',
    newRequest: 'Send New Request',
    partnerReqTitle: 'Submit Partner Request',
    companyPlaceholder: 'Your Company',
    namePlaceholder: 'Your Name',
    email: 'Email',
    companyEmailPlaceholder: 'company@email.com',
    phonePlaceholder: '+49 123 456 789',
    industry: 'Industry',
    industryPlaceholder: 'e.g. Electrical installation, Plumbing, etc.',
    companyMsgPlaceholder: 'Describe your company and your cooperation interests...',
    sendPartnerReq: 'Send Partner Request',
    errorTitle: 'Error',
    errorDesc: 'Please try again.',
    // Employee section
    employeeTitle1: 'Become an ',
    employeeTitle2: 'Employee',
    employeeSubtitle: 'Start your career at Gravante – one of the leading companies in the renewable energy sector',
    eFairPay: 'Fair Pay',
    eFairPayDesc: 'Above-average compensation and on-time payment',
    eFlexHours: 'Flexible Working Hours',
    eFlexHoursDesc: 'Work-life balance and predictable working hours',
    eFurtherEdu: 'Further Training',
    eFurtherEduDesc: 'Regular training and certification opportunities',
    eSecureJob: 'Secure Job',
    eSecureJobDesc: 'Long-term prospects in a growing company',
    positions: ['Roofer (m/f/d)', 'Electrician (m/f/d)', 'Heating Engineer (m/f/d)', 'Solar Technician (m/f/d)', 'Helper (m/f/d)', 'Project Manager (m/f/d)'],
    openPositions: 'Open Positions',
    noteLabel: 'Note:',
    noteDesc: 'Career changers and beginners are also warmly welcome!',
    applicationSent: 'Your application has been sent successfully.',
    newApplication: 'Send New Application',
    nameLabel: 'Name',
    yourEmailPlaceholder: 'your@email.com',
    positionLabel: 'Position',
    positionPlaceholder: 'e.g. Roofer',
    workExp: 'Work Experience (Years)',
    workExpPlaceholder: 'e.g. 5 years',
    aboutYouPlaceholder: 'Tell us about yourself...',
    submitApplication: 'Submit Application'
  }
};

const PartnerHero = () => {
  const { language } = useLanguage();
  const t = partnerTranslations[language] || partnerTranslations.de;

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&q=80"
          alt="Partner werden"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/75 to-slate-900/65"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {t.heroTitle}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-amber-400">
                {t.heroTitle2}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => document.getElementById('bewerbung').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                className="bg-gradient-to-r from-sky-500 to-amber-500 hover:from-sky-600 hover:to-amber-600 text-white px-8 py-6 text-lg group shadow-xl"
              >
                {t.applyNow}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => document.getElementById('vorteile').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                {t.benefits}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">50+</div>
                <div className="text-sm text-gray-400">{t.partners}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">25+</div>
                <div className="text-sm text-gray-400">{t.cities}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">40%</div>
                <div className="text-sm text-gray-400">{t.growth}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

const UnternehmensPartner = () => {
  const { language } = useLanguage();
  const t = partnerTranslations[language] || partnerTranslations.de;
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firma: '',
    ansprechpartner: '',
    email: '',
    telefon: '',
    branche: '',
    nachricht: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_type: 'partner',
          name: formData.ansprechpartner,
          email: formData.email,
          phone: formData.telefon,
          company: formData.firma,
          industry: formData.branche,
          message: formData.nachricht
        }),
      });
      
      if (response.ok) {
        setIsSuccess(true);
        setFormData({ firma: '', ansprechpartner: '', email: '', telefon: '', branche: '', nachricht: '' });
      } else {
        throw new Error('Fehler');
      }
    } catch (error) {
      toast({
        title: t.errorTitle,
        description: t.errorDesc,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const vorteile = [
    {
      icon: TrendingUp,
      title: t.cGrowth,
      description: t.cGrowthDesc
    },
    {
      icon: Users,
      title: t.cNetwork,
      description: t.cNetworkDesc
    },
    {
      icon: Award,
      title: t.cQuality,
      description: t.cQualityDesc
    },
    {
      icon: Briefcase,
      title: t.cProject,
      description: t.cProjectDesc
    }
  ];

  return (
    <section id="unternehmen" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 rounded-full mb-4">
            <Briefcase className="w-8 h-8 text-sky-600" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t.companyTitle1}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.companyTitle2}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.companySubtitle}
          </p>
        </div>

        {/* Vorteile */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {vorteile.map((vorteil, index) => {
            const Icon = vorteil.icon;
            return (
              <Card key={index} className="border-0 bg-gradient-to-br from-slate-50 to-blue-50 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-md">
                    <Icon className="w-6 h-6 text-sky-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{vorteil.title}</h3>
                  <p className="text-gray-600 text-sm">{vorteil.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Registrierungsformular */}
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-sky-100 shadow-xl">
            <CardContent className="p-8">
              {isSuccess ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.successTitle}</h3>
                  <p className="text-gray-600 mb-4">
                    {t.partnerReqSent}
                  </p>
                  <p className="text-gray-600 mb-6">
                    {t.contactSoon}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                    <a href="tel:+4915782283963" className="flex items-center justify-center gap-2 text-sky-600 hover:text-sky-700 font-semibold">
                      <Phone className="w-5 h-5" />
                      +49 157 82283963
                    </a>
                    <a href="mailto:Buchhaltung@gravante.de" className="flex items-center justify-center gap-2 text-sky-600 hover:text-sky-700 font-semibold">
                      <Mail className="w-5 h-5" />
                      Buchhaltung@gravante.de
                    </a>
                  </div>
                  <Button 
                    onClick={() => setIsSuccess(false)}
                    variant="outline"
                    className="border-sky-600 text-sky-600 hover:bg-sky-50"
                  >
                    {t.newRequest}
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.partnerReqTitle}</h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.company} *
                        </label>
                        <Input
                          type="text"
                          name="firma"
                          value={formData.firma}
                          onChange={handleChange}
                          required
                          placeholder={t.companyPlaceholder}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.contactPerson} *
                        </label>
                        <Input
                          type="text"
                          name="ansprechpartner"
                          value={formData.ansprechpartner}
                          onChange={handleChange}
                          required
                          placeholder={t.namePlaceholder}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.email} *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder={t.companyEmailPlaceholder}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.phone} *
                        </label>
                        <Input
                          type="tel"
                          name="telefon"
                          value={formData.telefon}
                          onChange={handleChange}
                          required
                          placeholder={t.phonePlaceholder}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.industry} *
                      </label>
                      <Input
                        type="text"
                        name="branche"
                        value={formData.branche}
                        onChange={handleChange}
                        required
                        placeholder={t.industryPlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.message}
                      </label>
                      <Textarea
                        name="nachricht"
                        value={formData.nachricht}
                        onChange={handleChange}
                        rows={4}
                        placeholder={t.companyMsgPlaceholder}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t.sending}
                        </>
                      ) : (
                        t.sendPartnerReq
                      )}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const MitarbeiterSection = () => {
  const { language } = useLanguage();
  const t = partnerTranslations[language] || partnerTranslations.de;
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [bewerbungData, setBewerbungData] = useState({
    name: '',
    email: '',
    telefon: '',
    position: '',
    erfahrung: '',
    nachricht: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_type: 'bewerbung',
          name: bewerbungData.name,
          email: bewerbungData.email,
          phone: bewerbungData.telefon,
          position: bewerbungData.position,
          experience: bewerbungData.erfahrung,
          message: bewerbungData.nachricht
        }),
      });
      
      if (response.ok) {
        setIsSuccess(true);
        setBewerbungData({ name: '', email: '', telefon: '', position: '', erfahrung: '', nachricht: '' });
      } else {
        throw new Error('Fehler');
      }
    } catch (error) {
      toast({
        title: t.errorTitle,
        description: t.errorDesc,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setBewerbungData({ ...bewerbungData, [e.target.name]: e.target.value });
  };

  const vorteile = [
    {
      icon: Heart,
      title: t.eFairPay,
      description: t.eFairPayDesc
    },
    {
      icon: Calendar,
      title: t.eFlexHours,
      description: t.eFlexHoursDesc
    },
    {
      icon: TrendingUp,
      title: t.eFurtherEdu,
      description: t.eFurtherEduDesc
    },
    {
      icon: Shield,
      title: t.eSecureJob,
      description: t.eSecureJobDesc
    }
  ];

  const positionen = t.positions;

  return (
    <section id="mitarbeiter" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <Users className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t.employeeTitle1}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.employeeTitle2}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.employeeSubtitle}
          </p>
        </div>

        {/* Vorteile */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {vorteile.map((vorteil, index) => {
            const Icon = vorteil.icon;
            return (
              <Card key={index} className="border-0 bg-white hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-sky-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{vorteil.title}</h3>
                  <p className="text-gray-600 text-sm">{vorteil.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Offene Positionen & Bewerbungsformular */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Offene Positionen */}
          <Card className="border-2 border-gray-100 shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.openPositions}</h3>
              <div className="space-y-3">
                {positionen.map((position, index) => (
                  <div key={index} className="flex items-center p-4 bg-gradient-to-r from-sky-50 to-amber-50 rounded-lg">
                    <Wrench className="w-5 h-5 text-sky-600 mr-3 flex-shrink-0" />
                    <span className="font-medium text-gray-900">{position}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-gray-700">
                  <strong>{t.noteLabel}</strong> {t.noteDesc}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Bewerbungsformular */}
          <Card className="border-2 border-amber-100 shadow-xl">
            <CardContent className="p-8">
              {isSuccess ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.successTitle}</h3>
                  <p className="text-gray-600 mb-4">
                    {t.applicationSent}
                  </p>
                  <p className="text-gray-600 mb-6">
                    {t.contactSoon}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                    <a href="tel:+4915782283963" className="flex items-center justify-center gap-2 text-amber-600 hover:text-amber-700 font-semibold">
                      <Phone className="w-5 h-5" />
                      +49 157 82283963
                    </a>
                    <a href="mailto:Buchhaltung@gravante.de" className="flex items-center justify-center gap-2 text-amber-600 hover:text-amber-700 font-semibold">
                      <Mail className="w-5 h-5" />
                      Buchhaltung@gravante.de
                    </a>
                  </div>
                  <Button 
                    onClick={() => setIsSuccess(false)}
                    variant="outline"
                    className="border-amber-600 text-amber-600 hover:bg-amber-50"
                  >
                    {t.newApplication}
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.applyNow}</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.nameLabel} *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={bewerbungData.name}
                        onChange={handleChange}
                        required
                        placeholder={t.namePlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.email} *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={bewerbungData.email}
                        onChange={handleChange}
                        required
                        placeholder={t.yourEmailPlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.phone} *
                      </label>
                      <Input
                        type="tel"
                        name="telefon"
                        value={bewerbungData.telefon}
                        onChange={handleChange}
                        required
                        placeholder={t.phonePlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.positionLabel} *
                      </label>
                      <Input
                        type="text"
                        name="position"
                        value={bewerbungData.position}
                        onChange={handleChange}
                        required
                        placeholder={t.positionPlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.workExp}
                      </label>
                      <Input
                        type="text"
                        name="erfahrung"
                        value={bewerbungData.erfahrung}
                        onChange={handleChange}
                        placeholder={t.workExpPlaceholder}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.message}
                      </label>
                      <Textarea
                        name="nachricht"
                        value={bewerbungData.nachricht}
                        onChange={handleChange}
                        rows={3}
                        placeholder={t.aboutYouPlaceholder}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t.sending}
                        </>
                      ) : (
                        t.submitApplication
                      )}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const PartnerPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <PartnerHero />
      <UnternehmensPartner />
      <MitarbeiterSection />
      <Footer />
    </div>
  );
};

export default PartnerPage;
